import React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown, UserPlus } from "lucide-react";
import { initials } from "@/lib/ui";

export const assigneeIds = (t) => t?.assignee_ids || (t?.assignee_id ? [t.assignee_id] : []);

export function AvatarStack({ ids = [], users = {}, size = "w-6 h-6", max = 3, testid }) {
  const list = ids.map((i) => users[i]).filter(Boolean);
  if (!list.length) return null;
  return (
    <div className="flex -space-x-1.5" data-testid={testid} title={list.map((u) => u.name).join(", ")}>
      {list.slice(0, max).map((u) => (
        <Avatar key={u.id} className={`${size} ring-2 ring-white`}>
          <AvatarImage src={u.avatar} />
          <AvatarFallback className="bg-indigo-100 text-indigo-700 text-[9px]">{initials(u.name)}</AvatarFallback>
        </Avatar>
      ))}
      {list.length > max && <span className={`${size} rounded-full ring-2 ring-white bg-slate-100 text-slate-600 text-[9px] font-semibold flex items-center justify-center`}>+{list.length - max}</span>}
    </div>
  );
}

export function AssigneePicker({ value = [], onChange, staff = [], testid = "assignee-picker" }) {
  const byId = Object.fromEntries(staff.map((u) => [u.id, u]));
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  const names = value.map((i) => byId[i]?.name).filter(Boolean);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="w-full h-9 px-3 rounded-md border border-input bg-white text-sm flex items-center gap-2 text-left hover:border-slate-300" data-testid={testid}>
          {names.length ? <AvatarStack ids={value} users={byId} size="w-5 h-5" /> : <UserPlus className="w-4 h-4 text-slate-400" />}
          <span className={`flex-1 truncate ${names.length ? "text-slate-800" : "text-slate-400"}`}>{names.length ? names.join(", ") : "Unassigned"}</span>
          <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-1.5 max-h-72 overflow-y-auto scroll-thin" data-testid={`${testid}-list`}>
        {staff.map((u) => (
          <label key={u.id} className="flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-slate-50 cursor-pointer" data-testid={`${testid}-option-${u.id}`}>
            <Checkbox checked={value.includes(u.id)} onCheckedChange={() => toggle(u.id)} />
            <Avatar className="w-6 h-6"><AvatarImage src={u.avatar} /><AvatarFallback className="bg-indigo-100 text-indigo-700 text-[9px]">{initials(u.name)}</AvatarFallback></Avatar>
            <span className="text-sm text-slate-700 truncate">{u.name}</span>
          </label>
        ))}
        {value.length > 0 && <button type="button" onClick={() => onChange([])} className="w-full text-left text-xs text-slate-500 hover:text-red-600 px-2 py-1.5 border-t border-slate-100 mt-1" data-testid={`${testid}-clear`}>Clear all</button>}
      </PopoverContent>
    </Popover>
  );
}
